import {
  Avatar,
  Button,
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioGroupField,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  TagGroup,
  useModal,
  withColumnGutters,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { dateCell, SearchBox } from "src/components";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { GuidedChangeEventConfigType } from "src/routes/projects/change-events/guided/utils";
import { queryResult } from "src/utils";
import { emptyCellDash } from "src/utils/constants";
import { SelectConfigurationStep_StructureFragment } from "../1.SelectConfigurationStep.generated";
import {
  SelectConfiguration_PotentialCheckoutConfigFragment,
  SelectConfiguration_PotentialProductOfferingConfigFragment,
  SelectConfiguration_ReadyPlanConfigLikeFragment,
  SelectConfiguration_SelectedConfig_ReadyPlanConfigLikeFragment,
  useSelectConfiguration_PotentialConfigsQuery,
  useSelectConfiguration_SelectedConfigLazyQuery,
} from "./SelectConfigurationModal.generated";

type SelectConfigurationModalProps = {
  configType: GuidedChangeEventConfigType;
  structure: SelectConfigurationStep_StructureFragment;
  initialSelectedConfigId?: string;
  onSelect: (config: SelectConfiguration_SelectedConfig_ReadyPlanConfigLikeFragment) => void;
};

export function SelectConfigurationModal(props: SelectConfigurationModalProps) {
  const { configType, structure, initialSelectedConfigId, onSelect } = props;
  const [search, setSearch] = useState<string | undefined>(undefined);

  const isCheckoutConfig = configType === GuidedChangeEventConfigType.CheckoutConfig;
  const query = useSelectConfiguration_PotentialConfigsQuery({
    variables: { structureId: structure.id, communityId: structure.community.id, isCheckoutConfig },
  });

  return queryResult(query, ({ checkoutConfigs, productOfferingConfigs }) => (
    <SelectConfigurationModalView
      structure={structure}
      checkoutConfigs={checkoutConfigs?.entities ?? []}
      productOfferingConfigs={productOfferingConfigs?.entities ?? []}
      initialSelectedConfigId={initialSelectedConfigId}
      onSelect={onSelect}
      search={search}
      setSearch={setSearch}
    />
  ));
}

type SelectConfigurationModalViewProps = Pick<
  SelectConfigurationModalProps,
  "initialSelectedConfigId" | "onSelect" | "structure"
> & {
  checkoutConfigs: SelectConfiguration_PotentialCheckoutConfigFragment[];
  productOfferingConfigs: SelectConfiguration_PotentialProductOfferingConfigFragment[];
  search: string | undefined;
  setSearch: (search: string) => void;
};

function SelectConfigurationModalView(props: SelectConfigurationModalViewProps) {
  const { structure, checkoutConfigs, productOfferingConfigs, initialSelectedConfigId, onSelect, search, setSearch } =
    props;
  const { closeModal } = useModal();
  const [fetchSelectedConfig] = useSelectConfiguration_SelectedConfigLazyQuery();

  // Defaults to "" rather than undefined so the radios stay controlled from the first render
  const [selectedConfigId, setSelectedConfigId] = useState(initialSelectedConfigId ?? "");

  const rows = useMemo(
    () => createRows(checkoutConfigs, productOfferingConfigs, structure.isAdu),
    [checkoutConfigs, productOfferingConfigs, structure],
  );
  const columns = useMemo(
    () => withColumnGutters(createColumns(selectedConfigId, setSelectedConfigId)),
    [selectedConfigId],
  );

  return (
    <>
      <ModalHeader>Select the Configuration to Replace</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.$}>
            {/* The wizard replaces its own url on mount, so a `?search=` write would clobber the draft's edit url. */}
            <SearchBox onSearch={setSearch} updateQueryString={false} />
          </div>
          <GridTable
            columns={columns}
            rows={rows}
            stickyHeader
            filter={search}
            fallbackMessage="There are no configurations available for this structure yet."
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Select"
          data-testid="selectConfiguration"
          disabled={!selectedConfigId && "Select a configuration"}
          onClick={async () => {
            // Fetch the selected config data to pass to the onSelect callback so it can store the proposed RPOs on form-state
            const { data } = await fetchSelectedConfig({ variables: { configId: selectedConfigId } });
            const selectedConfig = data?.readyPlanConfigLike;
            if (selectedConfig) onSelect(selectedConfig);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type ConfigRow = SelectConfiguration_ReadyPlanConfigLikeFragment & {
  version?: string;
  planName?: string;
  createdBy?: { id: string; name: string; avatar?: string };
};
type Row = SimpleHeaderAndData<ConfigRow>;

function createColumns(selectedConfigId: string, onSelect: (configId: string) => void): GridColumn<Row>[] {
  return [
    column<Row>({
      header: emptyCell,
      // A single-option `RadioGroupField` per row, all reading the same `selectedConfigId`, so only the
      // selected row's radio is checked. Beam has no radio equivalent of `selectColumn`.
      // TODO: replace this once GridTable supports radio button selection natively.
      data: ({ id, name }) => (
        <RadioGroupField
          label={name ?? ""}
          labelStyle="hidden"
          value={selectedConfigId}
          onChange={onSelect}
          options={[{ label: "", value: id }]}
          data-testid="configRadio"
        />
      ),
      w: "1px",
      align: "center",
    }),
    column<Row>({ header: "Configuration", data: ({ name }) => name }),
    column<Row>({
      header: "Created By",
      data: ({ createdBy }) => ({
        content: <Avatar src={createdBy?.avatar} name={createdBy?.name} showName />,
        value: createdBy?.name,
      }),
    }),
    column<Row>({ header: "Plan", data: ({ planName }) => planName ?? emptyCellDash }),
    column<Row>({ header: "Exterior", data: ({ exteriorOptionNames }) => tagCell(exteriorOptionNames) }),
    column<Row>({ header: "Interior", data: ({ interiorOptionNames }) => tagCell(interiorOptionNames) }),
    column<Row>({ header: "Version", data: ({ version }) => version ?? emptyCellDash, w: "70px" }),
    dateColumn<Row>({ header: "Last Updated", data: ({ updatedAt }) => dateCell(updatedAt), w: "120px" }),
  ];
}

function createRows(
  checkoutConfigs: SelectConfiguration_PotentialCheckoutConfigFragment[],
  productOfferingConfigs: SelectConfiguration_PotentialProductOfferingConfigFragment[],
  isAdu: boolean,
): GridDataRow<Row>[] {
  const rows = [
    // An ADU structure can only apply a checkout config that has an ADU side to it
    ...checkoutConfigs
      .filter((cc) => !isAdu || !!cc.aduProductOffering)
      .map((cc) => {
        // A checkout config covers both sides of the lot, so an ADU structure reads the ADU side of it
        const aggregateVersion = isAdu ? cc.aduAggregateVersion : cc.aggregateVersion;
        const productOffering = isAdu ? cc.aduProductOffering : cc.productOffering;
        const exteriorOptionNames = isAdu ? cc.aduExteriorOptionNames : cc.exteriorOptionNames;
        const interiorOptionNames = isAdu ? cc.aduInteriorOptionNames : cc.interiorOptionNames;
        return {
          id: cc.id,
          name: cc.name ?? emptyCellDash,
          createdBy: cc.createdBy?.internalUser ?? undefined,
          planName: productOffering?.name,
          exteriorOptionNames,
          interiorOptionNames,
          version: aggregateVersion?.displayVersion,
          updatedAt: cc.updatedAt,
        };
      }),
    // Only live configurations from an active offering can be applied
    ...productOfferingConfigs
      .filter((poc) => !poc.archive && poc.productOffering.status.code === ReadyPlanStatus.Active)
      .map((poc) => ({
        id: poc.id,
        name: poc.name,
        createdBy: poc.createdBy.internalUser ?? undefined,
        planName: poc.productOffering.name,
        exteriorOptionNames: poc.exteriorOptionNames,
        interiorOptionNames: poc.interiorOptionNames,
        version: poc.productOffering.aggregateCheckout?.displayVersion,
        updatedAt: poc.updatedAt,
      })),
  ];

  return [simpleHeader, ...simpleDataRows(rows)];
}

function tagCell(names: string[]) {
  if (names.isEmpty) return emptyCellDash;
  // `value` keeps the cell searchable, since GridTable's `filter` only matches string/number cells
  return { content: <TagGroup tags={names.map((text) => ({ text }))} />, value: names.join(" ") };
}
